'use client';

import Link from 'next/link';
import MainTabsUnderbar from './main-tabs-underbar';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo, useRef } from 'react';
import './main-tabs.css';

const TAB_OPTIONS = [
  { label: '탐색', href: '/', replace: true, width: 30 },
  { label: '나의여행', href: '/myTravels', replace: true, width: 59 },
  { label: '저장된 장소', href: '/savedPois', replace: true, width: 78 },
  { label: '커뮤니티', href: '/board/sharing-trips', replace: false, width: 59 },
];

export default function MainTabs() {
  const pathname = usePathname();
  const indexRef = useRef<number>(0);

  const selectedIndex = useMemo(() => {
    const index = TAB_OPTIONS.findIndex((item) => item.href === pathname);

    if (index !== -1) {
      indexRef.current = index;
    }

    return indexRef.current;
  }, [pathname]);

  const underbarWidthAndPosition = useMemo(() => {
    const gap = 24;
    const xPosition =
      TAB_OPTIONS.slice(0, selectedIndex).reduce((acc, item) => acc + item.width, 0) + gap * selectedIndex;
    const width = TAB_OPTIONS[selectedIndex].width;

    return { xPosition, width };
  }, [selectedIndex]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.currentTarget.parentElement?.dataset.isScrolling === 'true') {
      e.preventDefault();
    }
  }, []);

  const handleMouseDownScrolling = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const slider = e.currentTarget;
    const startX = e.pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    function onMouseMove(e: MouseEvent) {
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;

      slider.scrollLeft = scrollLeft - walk;

      if (Math.abs(walk) > 1) {
        slider.dataset.isScrolling = 'true';
      }
    }

    function onMouseUp() {
      slider.dataset.isScrolling = 'false';

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <div className='max-w-480pxr w-full px-20pxr fixed z-10 bg-white top-56pxr h-52pxr flex border-b border-new-gray-300'>
      <div
        className='relative flex w-full overflow-x-auto cursor-grab active:cursor-grabbing gap-24pxr'
        onMouseDown={handleMouseDownScrolling}
      >
        {TAB_OPTIONS.map((item, tabIndex) => {
          return (
            <Link
              href={`${item.href}`}
              replace={item.replace}
              scroll={true}
              prefetch={false}
              id={`tab-${item.href}`}
              key={`tab-${item.href}`}
              className={`main-tabs-item bg-white h-full shrink-0 flex items-center justify-center ${
                selectedIndex === tabIndex ? 'text-new-point-500 body-16-md' : 'text-new-gray-700 body-16-regular'
              }`}
              onClick={handleClick}
            >
              {item.label}
            </Link>
          );
        })}
        <MainTabsUnderbar underbarWidthAndPosition={underbarWidthAndPosition} />
      </div>
    </div>
  );
}
